"use client"

import type { ReactNode } from "react"
import { Mail, Send, type LucideIcon } from "lucide-react"
import { Switch } from "@/components/ui/switch"
import type { NotificationChannelType } from "@/lib/notifications/types"
import { useTranslations } from "next-intl"


interface NotificationToggle {
  /** The switch's accessible name: the visible label alone does not say what switching does. */
  label: string
  enabled: boolean
  disabled: boolean
  onChange: (enabled: boolean) => void
}

interface NotificationRowProps {
  label: string
  description: string
  badges?: ReactNode
  channelTypes?: readonly NotificationChannelType[]
  /** Omitted for a message that cannot be switched off. */
  toggle?: NotificationToggle
  children?: ReactNode
}

/** One notification of the System Notifications list; its switch saves at once. */
export function NotificationRow({ label, description, badges, channelTypes, toggle, children }: NotificationRowProps) {
  return (
    <div className="space-y-3 px-4 py-4 sm:px-5">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="break-words text-sm font-medium">{label}</h3>
            {badges}
          </div>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
        <div className="flex shrink-0 items-center gap-3 pt-0.5">
          {channelTypes ? <ChannelIcons types={channelTypes} /> : null}
          {toggle ? (
            <Switch
              checked={toggle.enabled}
              disabled={toggle.disabled}
              onCheckedChange={toggle.onChange}
              aria-label={toggle.label}
            />
          ) : null}
        </div>
      </div>
      {children}
    </div>
  )
}

function ChannelIcons({ types }: { types: readonly NotificationChannelType[] }) {
  const tUi = useTranslations("SystemNotifications.SystemNotificationsNotificationRow")

  return (
    <ul className="flex items-center gap-1.5 text-muted-foreground" aria-label={tUi("channels")}>
      {types.map((type) => {
        const { icon: Icon, label } = channelTypeDisplay(type, tUi)
        return (
          <li key={type} title={label}>
            <Icon className="h-4 w-4" aria-hidden />
            <span className="sr-only">{label}</span>
          </li>
        )
      })}
    </ul>
  )
}

function channelTypeDisplay(type: NotificationChannelType, tUi: ReturnType<typeof useTranslations<"SystemNotifications.SystemNotificationsNotificationRow">>): { icon: LucideIcon; label: string } {
  switch (type) {
    case "email":
      return { icon: Mail, label: tUi("email") }
    case "telegram":
      return { icon: Send, label: "Telegram" }
  }
}
